import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '@/api'

interface ProgramApplicationDetail {
  application_id: number
  program_name: string
  session_name: string | null
  applicant_login_id: string | null
  applicant_name: string | null
  phone: string | null
  school: string | null
  grade: string | null
  email: string | null
  status: string
  require_name: number
  require_phone: number
  require_email: number
  require_affiliation: number
  require_grade: number
}

const STATUS_STYLE: Record<string, string> = {
  '신청': 'bg-[#fff8e1] text-[#e67e22]',
  '예약완료': 'bg-[#e8f5e9] text-[#388e3c]',
  '신청취소': 'bg-[#f5f5f5] text-[#9e9e9e]',
}

export default function ProgramApplicationDetail() {
  const { program_id, application_id } = useParams<{ program_id: string; application_id: string }>()
  const navigate = useNavigate()

  const [detail, setDetail] = useState<ProgramApplicationDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => {
    fetchDetail()
  }, [application_id])

  const fetchDetail = async () => {
    setLoading(true)
    try {
      const res = await fetchWithAuth(
        getApiUrl(`/marubit/api/program/admin/${program_id}/applications/${application_id}`)
      )
      if (!res.ok) throw new Error('조회 실패')
      const data: ProgramApplicationDetail = await res.json()
      setDetail(data)
    } catch {
      alert('신청 정보를 불러오는 데 실패했습니다.')
      navigate(`/admin/program/${program_id}/applications`)
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async () => {
    if (!window.confirm('신청을 확인완료 처리하시겠습니까?')) return
    setActionLoading(true)
    try {
      const res = await fetchWithAuth(
        getApiUrl(`/marubit/api/program/admin/${program_id}/applications/${application_id}/approve`),
        { method: 'POST' }
      )
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '처리 실패') }
      fetchDetail()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '처리에 실패했습니다.')
    } finally {
      setActionLoading(false)
    }
  }

  const handleCancel = async () => {
    if (!window.confirm('신청을 취소 처리하시겠습니까?')) return
    setActionLoading(true)
    try {
      const res = await fetchWithAuth(
        getApiUrl(`/marubit/api/program/admin/${program_id}/applications/${application_id}/cancel`),
        { method: 'POST' }
      )
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '처리 실패') }
      fetchDetail()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '처리에 실패했습니다.')
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-[#f5f2f0] min-h-screen p-8 flex items-center justify-center">
        <p className="text-gray-300 text-[13px]">불러오는 중...</p>
      </div>
    )
  }

  if (!detail) return null

  const statusCls = STATUS_STYLE[detail.status] ?? 'bg-gray-100 text-gray-500'

  return (
    <div className="bg-[#f5f2f0] min-h-screen p-8">
      <div className="max-w-[1200px] mx-auto">

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">신청내역 상세</h1>
            <p className="text-[13px] text-[#887267] font-medium">{detail.program_name}</p>
            <p className="text-[13px] text-gray-400 mt-0.5">
              {new Date().toLocaleDateString('ko-KR', {
                year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
              })}
            </p>
          </div>
          <button
            onClick={() => navigate(`/admin/program/${program_id}/applications`)}
            className="text-[13px] text-[#887267] font-semibold px-4 py-2 rounded-lg border border-[#e8d8ce] bg-white cursor-pointer hover:bg-[#f5f2f0] transition-colors"
          >← 목록으로</button>
        </div>

        {/* Status Badge */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] px-6 py-4 mb-5 shadow-[0_2px_12px_rgba(136,114,103,0.06)] flex items-center gap-3">
          <span className="text-[13px] text-gray-400">신청 #{detail.application_id}</span>
          <span className={`text-[12px] px-2.5 py-1 rounded-lg font-semibold ${statusCls}`}>
            {detail.status}
          </span>
          {detail.session_name && (
            <span className="text-[12px] text-gray-400">회차: {detail.session_name}</span>
          )}
        </div>

        {/* Detail Card */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] px-6 py-6 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">

          <h2 className="text-[13px] font-bold text-[#5a3d32] mb-4">신청자 정보</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
            {/* 아이디(회원/비회원 구분)는 항상 표시, 나머지는 해당 프로그램의 필수 입력 항목만 노출 */}
            <InfoRow label="아이디" value={detail.applicant_login_id ?? '비회원'} />
            {detail.require_name === 1 && <InfoRow label="이름" value={detail.applicant_name ?? '-'} />}
            {detail.require_phone === 1 && <InfoRow label="전화번호" value={detail.phone ?? '-'} />}
            {detail.require_email === 1 && <InfoRow label="이메일" value={detail.email ?? '-'} />}
            {detail.require_affiliation === 1 && <InfoRow label="학교(소속)" value={detail.school ?? '-'} />}
            {detail.require_grade === 1 && <InfoRow label="학년" value={detail.grade ?? '-'} />}
          </div>

          {/* Actions */}
          {detail.status === '신청' && (
            <div className="border-t border-[#f5f2f0] mt-6 pt-5 flex justify-end gap-2">
              <button
                onClick={handleCancel}
                disabled={actionLoading}
                className="text-[13px] text-red-400 font-semibold px-5 py-2.5 rounded-lg border border-red-100 bg-white cursor-pointer hover:bg-red-50 transition-colors disabled:opacity-50"
              >취소 처리</button>
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className="bg-[#887267] text-white text-[13px] font-semibold px-6 py-2.5 rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50"
              >{actionLoading ? '처리 중...' : '확인 처리'}</button>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] text-[#5a3d32] font-medium">{label}</span>
      <span className="text-[13px] text-[#2a1f1a]">{value}</span>
    </div>
  )
}